import {onMounted, ref, shallowRef} from 'vue'

import {DEFAULT_KEY_MAPPINGS, STORAGE_KEY} from '@/components/harmonika'

import {
  getKeyboardLayoutMap,
  getLayoutAwareKeyLabelSync,
  initKeyboardLayout,
} from './keyboardLayout'

function cloneMappings(mappings: string[][]) {
  return mappings.map((row) => [...row])
}

function loadKeyMappings() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (!saved) return cloneMappings(DEFAULT_KEY_MAPPINGS)

    const parsed: unknown = JSON.parse(saved)
    if (Array.isArray(parsed) && Array.isArray(parsed[0])) {
      return parsed as string[][]
    }
  } catch (error) {
    console.error('Failed to load keyboard settings', error)
  }

  return cloneMappings(DEFAULT_KEY_MAPPINGS)
}

/**
 * Manages keyboard mappings with localStorage persistence.
 */
export function useKeyMappings() {
  const keyMappings = ref<string[][]>(loadKeyMappings())
  const layoutMap = shallowRef<Map<string, string>>(new Map())

  onMounted(() => {
    initKeyboardLayout()
      .then(() => getKeyboardLayoutMap())
      .then((map) => {
        layoutMap.value = map
      })
      .catch((error) => {
        console.warn('Keyboard layout detection failed', error)
      })
  })

  function persistMappings(nextMappings: string[][]) {
    keyMappings.value = nextMappings
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextMappings))
  }

  function updateMapping(rowIdx: number, btnIdx: number, keyCode: string) {
    const nextMappings = cloneMappings(keyMappings.value)

    while (nextMappings.length <= rowIdx) {
      nextMappings.push([])
    }

    const row = nextMappings[rowIdx] ?? []
    nextMappings[rowIdx] = row

    while (row.length <= btnIdx) {
      row.push('')
    }

    row[btnIdx] = keyCode
    persistMappings(nextMappings)
  }

  function clearMapping(rowIdx: number, btnIdx: number) {
    const nextMappings = cloneMappings(keyMappings.value)

    if (nextMappings[rowIdx]?.[btnIdx] !== undefined) {
      nextMappings[rowIdx][btnIdx] = ''
    }

    persistMappings(nextMappings)
  }

  function resetToDefaults() {
    persistMappings(cloneMappings(DEFAULT_KEY_MAPPINGS))
  }

  function getKeyCode(rowIdx: number, btnIdx: number) {
    const row = keyMappings.value[rowIdx]
    return row?.[btnIdx] ?? ''
  }

  function getKeyLabel(rowIdx: number, btnIdx: number) {
    const code = getKeyCode(rowIdx, btnIdx)
    return code ? getLayoutAwareKeyLabelSync(code, layoutMap.value) : ''
  }

  function findButtonByKeyCode(keyCode: string): [number, number] | null {
    for (let rowIdx = 0; rowIdx < keyMappings.value.length; rowIdx += 1) {
      const btnIdx = keyMappings.value[rowIdx]?.indexOf(keyCode) ?? -1
      if (btnIdx !== -1) {
        return [rowIdx, btnIdx]
      }
    }

    return null
  }

  return {
    keyMappings,
    layoutMap,
    updateMapping,
    clearMapping,
    resetToDefaults,
    getKeyCode,
    getKeyLabel,
    findButtonByKeyCode,
  }
}
